import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { formatXAF } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Fish, Trash2, Droplets, NotebookPen } from "lucide-react";
import { formatDate } from "@/lib/format";
import PondJournal from "@/components/layout/PondJournal";

export default function Pisciculture() {
  const { t, lang } = useI18n();
  const [ponds, setPonds] = useState([]);
  const [species, setSpecies] = useState([]);
  const [sites, setSites] = useState([]);
  const [openP, setOpenP] = useState(false);
  const [openS, setOpenS] = useState(false);
  const [newP, setNewP] = useState({ name: "", site_id: "", capacity_m3: "", species_id: "", stocked_date: "", initial_stock: "", current_stock: "" });
  const [newS, setNewS] = useState({ name: "", scientific_name: "", growth_months: 6 });
  const [journalPond, setJournalPond] = useState(null);
  const [pondReport, setPondReport] = useState([]);

  async function loadAll() {
    const [p, s, si, rep] = await Promise.all([
      api.get("/ponds"), api.get("/fish_species"), api.get("/sites"), api.get("/reports/ponds"),
    ]);
    setPonds(p.data); setSpecies(s.data); setSites(si.data); setPondReport(rep.data);
  }
  useEffect(() => { loadAll(); }, []);

  async function addPond() {
    if (!newP.name || !newP.species_id) return toast.error("Nom & espèce requis");
    const sp = species.find((s) => s.id === newP.species_id);
    const site = sites.find((s) => s.id === newP.site_id);
    await api.post("/ponds", {
      ...newP, capacity_m3: parseFloat(newP.capacity_m3 || 0),
      initial_stock: parseInt(newP.initial_stock || 0), current_stock: parseInt(newP.current_stock || newP.initial_stock || 0),
      species_name: sp?.name, site_name: site?.name,
      stocked_date: newP.stocked_date ? new Date(newP.stocked_date).toISOString() : new Date().toISOString(),
    });
    setOpenP(false); setNewP({ name: "", site_id: "", capacity_m3: "", species_id: "", stocked_date: "", initial_stock: "", current_stock: "" });
    toast.success(t("added")); loadAll();
  }
  async function addSpecies() {
    if (!newS.name) return toast.error("Nom requis");
    await api.post("/fish_species", { ...newS, growth_months: parseInt(newS.growth_months || 6) });
    setOpenS(false); setNewS({ name: "", scientific_name: "", growth_months: 6 });
    toast.success(t("added")); loadAll();
  }
  async function delPond(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/ponds/${id}`); toast.success(t("deleted")); loadAll();
  }

  return (
    <div className="space-y-6" data-testid="pisciculture-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">{t("pisciculture")}</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("ponds")}</h1>
          <p className="text-muted-foreground mt-1">{ponds.length} {t("ponds").toLowerCase()} · {species.length} {t("species").toLowerCase()}</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={openS} onOpenChange={setOpenS}>
            <DialogTrigger asChild><Button variant="outline" data-testid="add-species-btn"><Fish className="w-4 h-4 mr-2" />{t("species")}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("add")} — {t("species")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("name")}</Label><Input data-testid="species-name" value={newS.name} onChange={(e) => setNewS({ ...newS, name: e.target.value })} /></div>
                <div><Label>{t("scientific_name")}</Label><Input value={newS.scientific_name} onChange={(e) => setNewS({ ...newS, scientific_name: e.target.value })} /></div>
                <div><Label>Croissance (mois)</Label><Input type="number" value={newS.growth_months} onChange={(e) => setNewS({ ...newS, growth_months: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={addSpecies} data-testid="species-save">{t("save")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={openP} onOpenChange={setOpenP}>
            <DialogTrigger asChild><Button data-testid="add-pond-btn"><Plus className="w-4 h-4 mr-2" />{t("ponds")}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("add")} — {t("ponds")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>{t("name")}</Label><Input data-testid="pond-name" value={newP.name} onChange={(e) => setNewP({ ...newP, name: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>{t("site")}</Label>
                  <Select value={newP.site_id} onValueChange={(v) => setNewP({ ...newP, site_id: v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>{t("species")}</Label>
                  <Select value={newP.species_id} onValueChange={(v) => setNewP({ ...newP, species_id: v })}>
                    <SelectTrigger data-testid="pond-species"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{species.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("capacity")}</Label><Input type="number" value={newP.capacity_m3} onChange={(e) => setNewP({ ...newP, capacity_m3: e.target.value })} /></div>
                <div><Label>{t("stocked_date")}</Label><Input type="date" value={newP.stocked_date} onChange={(e) => setNewP({ ...newP, stocked_date: e.target.value })} /></div>
                <div><Label>Alevins initiaux</Label><Input type="number" value={newP.initial_stock} onChange={(e) => setNewP({ ...newP, initial_stock: e.target.value })} /></div>
                <div><Label>{t("current_stock")}</Label><Input type="number" value={newP.current_stock} onChange={(e) => setNewP({ ...newP, current_stock: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={addPond} data-testid="pond-save">{t("save")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
        {ponds.map((p) => (
          <Card key={p.id} className="card-hover border-border" data-testid={`pond-card-${p.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-eyebrow flex items-center gap-1"><Droplets className="w-3 h-3" />{p.site_name || "—"}</div>
                  <div className="text-xl font-bold mt-1">{p.name}</div>
                  <div className="text-sm text-muted-foreground">{p.species_name}</div>
                </div>
                <Badge variant="secondary">{p.capacity_m3} m³</Badge>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">{t("stocked_date")}</div>
                  <div className="font-medium">{formatDate(p.stocked_date, lang)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">{t("current_stock")}</div>
                  <div className="font-semibold text-primary">{p.current_stock} / {p.initial_stock}</div>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-1">
                <Button variant="outline" size="sm" onClick={() => setJournalPond(p)} data-testid={`pond-journal-${p.id}`}>
                  <NotebookPen className="w-4 h-4 mr-1" />{t("pond_journal")}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => delPond(p.id)} data-testid={`pond-delete-${p.id}`}>
                  <Trash2 className="w-4 h-4 mr-1" />{t("delete")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">{t("species")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {species.map((s) => (
              <div key={s.id} className="border border-border rounded-lg p-4">
                <div className="font-semibold">{s.name}</div>
                <div className="text-xs text-muted-foreground italic">{s.scientific_name}</div>
                <div className="text-sm mt-2">Croissance: {s.growth_months} mois</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card className="border-border" data-testid="pond-rentability">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{t("rentability")} — {t("ponds")}</h3>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/report.xlsx?type=ponds`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="export-ponds-btn">{t("export_excel")}</Button>
            </a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left border-b border-border">
                <tr>
                  <th className="py-2">{t("name")}</th>
                  <th>{t("species")}</th>
                  <th>{t("feed_kg")}</th>
                  <th>{t("feed_cost")}</th>
                  <th>{t("production")}</th>
                  <th>{t("revenue")}</th>
                  <th>{t("margin")}</th>
                </tr>
              </thead>
              <tbody>
                {pondReport.map((r) => (
                  <tr key={r.pond_id} className="border-b border-border/60 last:border-0" data-testid={`pond-report-${r.pond_id}`}>
                    <td className="py-2 font-medium">{r.name}</td>
                    <td>{r.species_name}</td>
                    <td>{r.feed_kg} kg</td>
                    <td>{formatXAF(r.cost)}</td>
                    <td>{r.production_kg} kg</td>
                    <td>{r.price_match ? formatXAF(r.revenue) : "—"}</td>
                    <td className={`font-bold ${r.price_match ? (r.margin >= 0 ? "text-primary" : "text-destructive") : "text-muted-foreground"}`}>
                      {r.price_match ? formatXAF(r.margin) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <PondJournal pond={journalPond} open={!!journalPond} onClose={() => setJournalPond(null)} />
    </div>
  );
}
